import React, { useState, useCallback, useRef, useEffect } from "react";
import { Button, Divider, Space, Tooltip, Popover, Input, Modal } from "antd";
import {
    BoldOutlined,
    ItalicOutlined,
    UnderlineOutlined,
    StrikethroughOutlined,
    OrderedListOutlined,
    UnorderedListOutlined,
    AlignLeftOutlined,
    AlignCenterOutlined,
    AlignRightOutlined,
    TableOutlined,
    UndoOutlined,
    RedoOutlined,
    PictureOutlined,
    LinkOutlined,
    LineOutlined,
    FontColorsOutlined,
} from "@ant-design/icons";
import { DEFAULT_TOOLBAR_BUTTONS, BUTTON_GROUPS } from "../../utils/constants";
import { insertImageViaFilePicker } from "../../extensions/ImageUploadExtension";
import "./MenuBar.css";

// 预定义颜色面板
const PRESET_COLORS = [
    "#000000", "#434343", "#666666", "#999999", "#B7B7B7", "#D9D9D9", "#FFFFFF",
    "#FF0000", "#FF4D4F", "#FA8C16", "#FADB14", "#52C41A", "#13C2C2", "#1890FF",
    "#2F54EB", "#722ED1", "#EB2F96", "#A0522D", "#FF7875", "#FFA940", "#FFF566",
    "#95DE64", "#5CDBD3", "#69C0FF", "#85A5FF", "#B37FEB", "#FF85C0", "#CD853F",
];

/**
 * 颜色选择器组件
 */
const ColorPicker = ({ editor, onClose }) => {
    const handleColorSelect = useCallback((color) => {
        editor.chain().focus().setColor(color).run();
        onClose?.();
    }, [editor, onClose]);

    const handleRemoveColor = useCallback(() => {
        editor.chain().focus().unsetColor().run();
        onClose?.();
    }, [editor, onClose]);

    return (
        <div style={{ width: 238, padding: 4 }}>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
                {PRESET_COLORS.map((color) => (
                    <div
                        key={color}
                        onClick={() => handleColorSelect(color)}
                        style={{
                            width: 28,
                            height: 28,
                            backgroundColor: color,
                            border: `1px solid ${color === "#FFFFFF" ? "#d9d9d9" : color}`,
                            borderRadius: 3,
                            cursor: "pointer",
                            transition: "transform 0.15s",
                        }}
                        onMouseEnter={(e) => { e.target.style.transform = "scale(1.15)"; }}
                        onMouseLeave={(e) => { e.target.style.transform = "scale(1)"; }}
                    />
                ))}
            </div>
            <div style={{ marginTop: 8, borderTop: "1px solid #f0f0f0", paddingTop: 6 }}>
                <Button type="link" size="small" onClick={handleRemoveColor} style={{ padding: 0, fontSize: 12 }}>
                    移除颜色
                </Button>
            </div>
        </div>
    );
};

/**
 * 工具栏组件
 */
const MenuBar = ({ editor, enabledButtons = DEFAULT_TOOLBAR_BUTTONS }) => {
    const [colorPickerOpen, setColorPickerOpen] = useState(false);
    const [linkModalOpen, setLinkModalOpen] = useState(false);
    const [linkUrl, setLinkUrl] = useState("");

    if (!editor) return null;

    // 插入链接的处理
    const handleInsertLink = () => {
        // 获取当前选中文本的链接
        const previousUrl = editor.getAttributes("link").href || "";
        setLinkUrl(previousUrl);
        setLinkModalOpen(true);
    };

    const handleLinkConfirm = () => {
        if (linkUrl) {
            // 确保 URL 有协议前缀
            const url = linkUrl.match(/^https?:\/\//) ? linkUrl : `https://${linkUrl}`;
            editor.chain().focus().extendMarkRange("link").setLink({ href: url }).run();
        } else {
            editor.chain().focus().extendMarkRange("link").unsetLink().run();
        }
        setLinkModalOpen(false);
        setLinkUrl("");
    };

    const handleLinkCancel = () => {
        setLinkModalOpen(false);
        setLinkUrl("");
    };

    // 获取当前字体颜色，用于按钮下划线指示
    const currentColor = editor.getAttributes("textStyle").color || "#000000";

    const buttonsConfig = {
        bold: (
            <Tooltip title="加粗 (Ctrl+B)" placement="bottom">
                <Button 
                    key="bold" 
                    type={editor.isActive("bold") ? "primary" : "default"} 
                    icon={<BoldOutlined />} 
                    onClick={() => editor.chain().focus().toggleBold().run()} 
                />
            </Tooltip>
        ),
        italic: (
            <Tooltip title="斜体 (Ctrl+I)" placement="bottom">
                <Button 
                    key="italic" 
                    type={editor.isActive("italic") ? "primary" : "default"} 
                    icon={<ItalicOutlined />} 
                    onClick={() => editor.chain().focus().toggleItalic().run()} 
                />
            </Tooltip>
        ),
        underline: (
            <Tooltip title="下划线 (Ctrl+U)" placement="bottom">
                <Button 
                    key="underline" 
                    type={editor.isActive("underline") ? "primary" : "default"} 
                    icon={<UnderlineOutlined />} 
                    onClick={() => editor.chain().focus().toggleUnderline().run()} 
                />
            </Tooltip>
        ),
        strikethrough: (
            <Tooltip title="删除线" placement="bottom">
                <Button 
                    key="strikethrough" 
                    type={editor.isActive("strike") ? "primary" : "default"} 
                    icon={<StrikethroughOutlined />} 
                    onClick={() => editor.chain().focus().toggleStrike().run()} 
                />
            </Tooltip>
        ),
        fontColor: (
            <Popover
                key="fontColor"
                content={<ColorPicker editor={editor} onClose={() => setColorPickerOpen(false)} />}
                trigger="click"
                open={colorPickerOpen}
                onOpenChange={setColorPickerOpen}
                placement="bottom"
            >
                <Tooltip title="字体颜色" placement="bottom">
                    <Button
                        icon={<FontColorsOutlined />}
                        style={{
                            borderBottom: `3px solid ${currentColor}`,
                        }}
                    />
                </Tooltip>
            </Popover>
        ),
        alignLeft: (
            <Tooltip title="左对齐" placement="bottom">
                <Button 
                    key="alignLeft" 
                    type={editor.isActive({ textAlign: "left" }) ? "primary" : "default"} 
                    icon={<AlignLeftOutlined />} 
                    onClick={() => editor.chain().focus().setTextAlign("left").run()} 
                />
            </Tooltip>
        ),
        alignCenter: (
            <Tooltip title="居中对齐" placement="bottom">
                <Button 
                    key="alignCenter" 
                    type={editor.isActive({ textAlign: "center" }) ? "primary" : "default"} 
                    icon={<AlignCenterOutlined />} 
                    onClick={() => editor.chain().focus().setTextAlign("center").run()} 
                />
            </Tooltip>
        ),
        alignRight: (
            <Tooltip title="右对齐" placement="bottom">
                <Button 
                    key="alignRight" 
                    type={editor.isActive({ textAlign: "right" }) ? "primary" : "default"} 
                    icon={<AlignRightOutlined />} 
                    onClick={() => editor.chain().focus().setTextAlign("right").run()} 
                />
            </Tooltip>
        ),
        bulletList: (
            <Tooltip title="无序列表" placement="bottom">
                <Button 
                    key="bulletList" 
                    type={editor.isActive("bulletList") ? "primary" : "default"} 
                    icon={<UnorderedListOutlined />} 
                    onClick={() => editor.chain().focus().toggleBulletList().run()} 
                />
            </Tooltip>
        ),
        orderedList: (
            <Tooltip title="有序列表" placement="bottom">
                <Button 
                    key="orderedList" 
                    type={editor.isActive("orderedList") ? "primary" : "default"} 
                    icon={<OrderedListOutlined />} 
                    onClick={() => editor.chain().focus().toggleOrderedList().run()} 
                />
            </Tooltip>
        ),
        insertImage: (
            <Tooltip title="插入图片（也可直接粘贴图片）" placement="bottom">
                <Button 
                    key="insertImage" 
                    icon={<PictureOutlined />} 
                    onClick={() => insertImageViaFilePicker(editor)} 
                />
            </Tooltip>
        ),
        insertLink: (
            <Tooltip title="插入链接" placement="bottom">
                <Button 
                    key="insertLink" 
                    type={editor.isActive("link") ? "primary" : "default"} 
                    icon={<LinkOutlined />} 
                    onClick={handleInsertLink} 
                />
            </Tooltip>
        ),
        insertTable: (
            <Tooltip title="插入表格" placement="bottom">
                <Button 
                    key="insertTable" 
                    icon={<TableOutlined />} 
                    onClick={() => editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run()} 
                    disabled={!editor.can().insertTable} 
                />
            </Tooltip>
        ),
        deleteTable: (
            <Tooltip title="删除表格" placement="bottom">
                <Button 
                    key="deleteTable" 
                    icon={<TableOutlined style={{ color: "#ff4d4f" }} />} 
                    onClick={() => editor.chain().focus().deleteTable().run()} 
                    disabled={!editor.can().deleteTable} 
                />
            </Tooltip>
        ),
        horizontalRule: (
            <Tooltip title="水平分割线" placement="bottom">
                <Button 
                    key="horizontalRule" 
                    icon={<LineOutlined />} 
                    onClick={() => editor.chain().focus().setHorizontalRule().run()} 
                />
            </Tooltip>
        ),
        undo: (
            <Tooltip title="撤销 (Ctrl+Z)" placement="bottom">
                <Button 
                    key="undo" 
                    icon={<UndoOutlined />} 
                    onClick={() => editor.chain().focus().undo().run()} 
                    disabled={!editor.can().undo()} 
                />
            </Tooltip>
        ),
        redo: (
            <Tooltip title="重做 (Ctrl+Y)" placement="bottom">
                <Button 
                    key="redo" 
                    icon={<RedoOutlined />} 
                    onClick={() => editor.chain().focus().redo().run()} 
                    disabled={!editor.can().redo()} 
                />
            </Tooltip>
        ),
    };

    // 按钮分组渲染
    return (
        <>
            <div className="menu-bar-modern">
                {BUTTON_GROUPS.map((group, index) => {
                    const groupButtons = group.buttons.filter((btn) => enabledButtons.includes(btn));
                    if (groupButtons.length === 0) return null;

                    return (
                        <React.Fragment key={index}>
                            {index > 0 && <Divider type="vertical" className="menu-bar-divider" />}
                            <Space className="button-group" size="small">
                                {groupButtons.map((btnKey) => (
                                    <React.Fragment key={btnKey}>
                                        {buttonsConfig[btnKey]}
                                    </React.Fragment>
                                ))}
                            </Space>
                        </React.Fragment>
                    );
                })}
            </div>

            {/* 链接输入弹窗 */}
            <Modal
                title="插入链接"
                open={linkModalOpen}
                onOk={handleLinkConfirm}
                onCancel={handleLinkCancel}
                okText="确定"
                cancelText="取消"
                width={400}
                destroyOnHidden
            >
                <Input
                    placeholder="请输入链接地址，如 https://example.com"
                    value={linkUrl}
                    onChange={(e) => setLinkUrl(e.target.value)}
                    onPressEnter={handleLinkConfirm}
                    autoFocus
                />
                <div style={{ marginTop: 8, fontSize: 12, color: "#999" }}>
                    清空链接地址后点击确定可移除链接
                </div>
            </Modal>
        </>
    );
};

export default MenuBar;